import { lazy, Suspense, type ComponentType } from 'react';
import { Route } from 'react-router-dom';
import type { RouteConfig } from '@appTypes/route.types';
import { ProtectedRoute } from '@components/shared/ProtectedRoute';
import { PermissionGate } from '@components/shared/PermissionGate';
import { LoadingFallback } from '@components/shared/LoadingFallback';

type ModuleImport = { default: ComponentType };

const moduleMap: Record<string, () => Promise<ModuleImport>> = {
  login: () => import('@modules/login'),
  'auth-callback': () => import('@modules/auth-callback'),
  'reset-password': () => import('@modules/auth/ResetPasswordPage'),
  dashboard: () => import('@modules/dashboard'),
  'coverage-layout': () => import('@modules/coverage/CoverageLayout'),
  coverage: () => import('@modules/coverage'),
  'coverage-excel-imports': () => import('@modules/coverage/ExcelImportsPage'),
  'newsletters-layout': () => import('@modules/newsletters/NewslettersLayout'),
  newsletters: () => import('@modules/newsletters'),
  goals: () => import('@modules/goals'),
  'chat-agent': () => import('@modules/chat-agent'),
  settings: () => import('@modules/settings'),
  'configuration-layout': () => import('@modules/configuration/ConfigurationLayout'),
  'triage-weights': () => import('@modules/settings/TriageWeightsPage'),
  'tier-list': () => import('@modules/settings/TierListPage'),
  'brand-sov': () => import('@modules/settings/BrandSovPage'),
  'newsletter-topics': () => import('@modules/settings/NewsletterTopicsPage'),
  'redis-valkey': () => import('@modules/redis-valkey'),
  'audio-files': () => import('@modules/audio-files'),
  'admin-layout': () => import('@modules/admin'),
  'admin-users': () => import('@modules/admin/UsersPage'),
  'admin-email-dls': () => import('@modules/admin/EmailDLPage'),
  // Executive read-only surface (`/exec/*`). Lazy-loaded so they
  // never enter the bundle for admin/editor/viewer users.
  'exec-layout': () => import('@modules/exec/ExecLayout'),
  'exec-dashboard': () => import('@modules/exec/dashboard/ExecDashboardPage'),
  'exec-newsletters-daily': () =>
    import('@modules/exec/newsletters/ExecDailyNewsletter'),
  'exec-newsletters-monthly': () =>
    import('@modules/exec/newsletters/ExecMonthlyNewsletter'),
  'exec-newsletters-weekly': () =>
    import('@modules/exec/newsletters/ExecWeeklyNewsletter'),
  'exec-insights-sov': () => import('@modules/exec/insights/ExecInsightsSov'),
  'exec-insights-goals': () => import('@modules/exec/insights/ExecInsightsGoals'),
  'exec-coverage': () => import('@modules/exec/coverage/ExecCoveragePage'),
  'exec-chat': () => import('@modules/exec/chat/ExecChatSupportPage'),
};

function getComponent(moduleName: string): ComponentType {
  const importer = moduleMap[moduleName];
  if (!importer) {
    throw new Error(`Module "${moduleName}" not found in module map`);
  }
  return lazy(importer);
}

function wrapWithGuards(element: React.ReactNode, route: RouteConfig): React.ReactNode {
  let wrapped = element;

  // Note the `requiresDeveloper` arm: a developer-only route may declare no
  // permissions at all, and this gate — not the sidebar filter — is what stops
  // someone reaching the page by typing the URL.
  if ((route.permissions && route.permissions.length > 0) || route.requiresDeveloper) {
    wrapped = (
      <PermissionGate permissions={route.permissions} requireDeveloper={route.requiresDeveloper}>
        {wrapped}
      </PermissionGate>
    );
  }

  if (!route.public) {
    wrapped = <ProtectedRoute>{wrapped}</ProtectedRoute>;
  }

  return <Suspense fallback={<LoadingFallback />}>{wrapped}</Suspense>;
}

export function renderRoutes(routes: RouteConfig[]): React.ReactNode[] {
  return routes.map((route) => {
    const Component = getComponent(route.module);
    const element = wrapWithGuards(<Component />, route);

    if (route.children && route.children.length > 0) {
      return (
        <Route key={route.key} path={route.path} element={element}>
          {renderRoutes(route.children)}
        </Route>
      );
    }

    return <Route key={route.key} path={route.path} element={element} />;
  });
}
